import { useEffect, useState } from "react";
import { Save, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const CHOICE_LABELS = { company: "Company", contractor: "Contractor", customer: "Customer", completion: "Job completion", customer_payment: "Customer payment received", courts: "LA County courts", arbitration: "Binding arbitration" };

function TermInput({ f, value, onChange }) {
  const cls = "dg-input w-full mt-1 py-1.5 text-sm";
  if (f.kind.startsWith("choice:")) {
    return (
      <select className={cls} value={value} onChange={onChange} data-testid={`term-${f.key}`}>
        <option value="">— leave blank —</option>
        {f.kind.slice(7).split("|").map((o) => <option key={o} value={o}>{CHOICE_LABELS[o] || o}</option>)}
      </select>
    );
  }
  return <input className={cls} value={value} onChange={onChange} inputMode={f.kind === "text" ? "text" : "decimal"} placeholder={f.kind === "money" ? "e.g. 60" : f.kind === "number" ? "e.g. 14" : ""} data-testid={`term-${f.key}`} />;
}

/** Business terms that fill the agreement blanks; saved once, reused for every invite. */
export function AgreementTermsForm() {
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/agreements/terms").then((r) => { setData(r.data); setVals(r.data.values || {}); }).catch(() => setData(false)); }, []);
  if (!data) return null;

  const groups = [...new Set(data.fields.map((f) => f.group))];
  const filled = data.fields.filter((f) => (vals[f.key] || "").trim()).length;
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/agreements/terms", { values: vals }); setVals(r.data.values); toast.success("Business terms saved"); }
    catch { toast.error("Couldn't save terms"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="agreement-terms-form">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-slate-300">Business terms — <span className="text-slate-400">{filled}/{data.fields.length} filled</span>. Anything left empty stays a blank line in the agreement.</div>
        <a href={`${API}/admin/agreements/preview`} target="_blank" rel="noreferrer" className="dg-link text-xs ml-auto inline-flex items-center gap-1" data-testid="terms-preview-link"><Eye className="w-3 h-3" /> Preview draft PDF</a>
      </div>
      {groups.map((g) => (
        <div key={g} className="mt-4">
          <div className="dg-eyebrow mb-2">{g}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.fields.filter((f) => f.group === g).map((f) => (
              <label key={f.key} className="block text-xs text-slate-400">{f.label}<TermInput f={f} value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} /></label>
            ))}
          </div>
        </div>
      ))}
      <p className="text-[11px] text-slate-500 mt-3">Approved services: comma-separated, e.g. “Remote tech support, TV and display mounting, Wi-Fi / networking”.</p>
      <button type="button" onClick={save} disabled={busy} className="dg-btn-primary rounded-full px-5 py-2 text-sm mt-3 inline-flex items-center gap-2 disabled:opacity-50" data-testid="terms-save-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save business terms
      </button>
    </div>
  );
}
